import { Button, Card, Space, Spin, Switch, Table, Typography } from 'antd'
import { useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'

import { useUserGroups } from '@/hooks/useUserGroups'

import type { AddPricePrefill } from './components/AddPriceModal'
import { AddPriceModal } from './components/AddPriceModal'
import { EditPriceModal } from './components/EditPriceModal'
import { timelineColumns } from './components/priceColumns'
import { PriceDetailHeader } from './components/PriceDetailHeader'
import type { PriceEntry, PriceResourceType } from './components/priceUtils'
import { PriceWarnings } from './components/PriceWarnings'
import { useScheduleEntries } from './components/useScheduleEntries'

const { Text } = Typography

export default function PriceDetailPage() {
  const userGroups = useUserGroups()
  const {
    id: groupName,
    resourceType,
    comboKey,
  } = useParams<{ id: string; resourceType: string; comboKey: string }>()
  const [hideExpired, setHideExpired] = useState(true)
  const [addOpen, setAddOpen] = useState(false)
  const [editPriceId, setEditPriceId] = useState<string | null>(null)

  const rt = (resourceType ?? 'vm') as PriceResourceType
  const { entries, loading, clusterId, label, refetch } = useScheduleEntries(
    groupName ?? '',
    rt,
    comboKey ?? '',
  )

  const prefill = useMemo((): AddPricePrefill => {
    const parts = (comboKey ?? '').split(':')
    const base = {
      groupName: groupName ?? '',
      resourceType: rt,
      clusterId: parts[0],
    }
    if (rt === 'vm') return { ...base, vmConfigurationId: parts[1] }
    if (rt === 'storage') {
      return {
        ...base,
        storageType: parts[1] as 'HDD' | 'SSD' | 'NVME',
        replicated: parts[2] === 'true',
      }
    }
    return {
      ...base,
      addressType: parts[1] as 'V4' | 'V6',
    }
  }, [comboKey, rt, groupName])

  const overlaps = useMemo(() => {
    const a = entries
      .filter((e) => !e.expired)
      .sort((x, y) => x.activeSince.localeCompare(y.activeSince))
    return a.some(
      (e, i) =>
        i < a.length - 1 &&
        (e.activeUntil ?? '9999-12-31T23:59:59Z') > a[i + 1].activeSince,
    )
  }, [entries])

  if (!groupName || !comboKey) return null

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 48 }}>
        <Spin size="large" />
      </div>
    )
  }

  const filtered = hideExpired ? entries.filter((e) => !e.expired) : entries
  const hasIndefinite = entries.some((e) => !e.activeUntil)

  return (
    <div>
      <PriceDetailHeader
        label={label}
        clusterId={clusterId}
        groupName={groupName}
        onAdd={() => setAddOpen(true)}
      />

      <PriceWarnings
        entries={entries}
        hasIndefinite={hasIndefinite}
        overlaps={overlaps}
      />

      <Space size="small" style={{ marginBottom: 12 }}>
        <Text type="secondary">Hide expired</Text>
        <Switch checked={hideExpired} onChange={setHideExpired} />
      </Space>

      <Card>
        <Table<PriceEntry>
          dataSource={filtered}
          rowKey="id"
          pagination={false}
          size="small"
          scroll={{ x: 800 }}
          locale={{
            emptyText: 'No price entries for this combination',
          }}
          columns={[
            ...timelineColumns(),
            {
              title: '',
              width: 80,
              render: (_: unknown, record: PriceEntry) =>
                !record.expired ? (
                  <Button
                    type="link"
                    size="small"
                    onClick={() => setEditPriceId(record.id)}
                  >
                    Edit
                  </Button>
                ) : null,
            },
          ]}
        />
      </Card>

      {addOpen && (
        <AddPriceModal
          open
          groups={userGroups}
          prefill={prefill}
          onClose={() => setAddOpen(false)}
          onSuccess={() => {
            setAddOpen(false)
            refetch()
          }}
        />
      )}
      {editPriceId && (
        <EditPriceModal
          open
          resourceType={rt}
          priceId={editPriceId}
          onClose={() => setEditPriceId(null)}
          onSuccess={() => {
            setEditPriceId(null)
            refetch()
          }}
        />
      )}
    </div>
  )
}
